// ---------------------------------------------------------------------------
// useDesignSystem — 6-axis design system hook (Phase 365-01)
//
// Derives ThemeTokens from the 6-axis DesignSystemState using lookupPreset
// from the shared shadcn-presets module. oklch values pass through verbatim
// (no HSL conversion) per D-12.
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { useStudioStore } from '@/store/studio-store'
import type { DesignSystemState } from '../lib/design-system-options'
import { computeThemeTokens } from '../lib/theme-tokens'

/**
 * Compute AppV2 ThemeTokens from 6-axis state.
 *
 * Mirrors the derivation in BlockGridTestPage.tsx (lines 171-182):
 *   1. Call lookupPreset with the 6 axes.
 *   2. Split radius out of cssVars.light — everything else becomes `colors`.
 *   3. Return { colors, radius } as ThemeTokens. oklch pass-through.
 */
export function useDesignSystem() {
  const designSystem = useStudioStore((s) => s.designSystem)
  const setDesignSystemOption = useStudioStore((s) => s.setDesignSystemOption)
  const setDesignSystemBulk = useStudioStore((s) => s.setDesignSystemBulk)

  const themeTokens = useMemo(
    () => computeThemeTokens(designSystem),
    [designSystem],
  )

  return {
    designSystem,
    themeTokens,
    // Phase 396 fix: expose style name so the preview iframe can activate
    // the `.style-*` CSS class on its own <html> (shadcn-*-sync.css rules).
    styleName: designSystem.style,
    // Phase 396 fix: expose font name so the iframe can inject it into the
    // dynamic label of the card-typography-specimen block.
    fontName: designSystem.font,
    setOption: setDesignSystemOption,
    setBulk: setDesignSystemBulk,
  }
}

export { computeThemeTokens }
export type { DesignSystemState }
